import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { Preferences } from '@capacitor/preferences'
import { createNativeSocialLogin, NATIVE_SOCIAL_KEY, type NativeSocialFlow } from '~/utils/native-social-login'
import { deliverSocialCallback, setNativeSocialPending } from '~/composables/useSocialLogin'

export default defineNuxtPlugin((nuxtApp) => {
  if (!Capacitor.isNativePlatform()) return
  if (!Capacitor.isPluginAvailable('App') || !Capacitor.isPluginAvailable('Browser')) return
  const { bffFetch } = useMwBff()
  const localePath = useLocalePath()
  const flow = createNativeSocialLogin({
    async read() {
      const { value } = await Preferences.get({ key: NATIVE_SOCIAL_KEY })
      try { return value ? JSON.parse(value) as NativeSocialFlow : null }
      catch { await Preferences.remove({ key: NATIVE_SOCIAL_KEY }); return null }
    },
    write: value => Preferences.set({ key: NATIVE_SOCIAL_KEY, value: JSON.stringify(value) }),
    remove: () => Preferences.remove({ key: NATIVE_SOCIAL_KEY }),
    open: url => Browser.open({ url }),
    close: () => Browser.close(),
    exchange: (code, codeVerifier) => bffFetch('/whmcs/social/native/exchange', {
      method: 'POST', body: { code, codeVerifier }, retry: 0, timeout: 15000,
    }),
    async result(params) {
      await nuxtApp.runWithContext(async () => {
        if (deliverSocialCallback(params)) return
        await navigateTo(localePath({ path: '/auth/social/callback', query: params }), { replace: true })
      })
    },
    pending: setNativeSocialPending,
  })

  // One listener for the lifetime of the app, including login from checkout.
  // Register before consulting launchUrl; callbacks may arrive during startup.
  let ready!: () => void
  const mounted = new Promise<void>(resolve => { ready = resolve })
  const listeners = [
    App.addListener('appUrlOpen', ({ url }) => { void mounted.then(() => flow.handle(url)) }),
    App.addListener('appStateChange', ({ isActive }) => { if (isActive) flow.dismiss() }),
    Browser.addListener('browserFinished', () => flow.dismiss()),
  ]
  nuxtApp.hook('app:mounted', async () => {
    ready()
    await Promise.all(listeners)
    const launch = await App.getLaunchUrl()
    if (launch?.url) await flow.handle(launch.url)
  })
  if (import.meta.hot) import.meta.hot.dispose(() => {
    for (const listener of listeners) void listener.then(handle => handle.remove())
  })
  return { provide: { nativeSocialLogin: flow } }
})
